import { useEffect, useState } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Save, Send, CheckCircle2, XCircle, Plus, Trash2, Pencil, Download } from "lucide-react";
import { api, fileUrl, formatError, downloadBlob } from "@/lib/api";
import { Page } from "@/components/Page";
import { AdminBanners, AdminGallery, AdminCounters, AdminAbout, AdminContact, AdminAuthorisedUsers, AdminFeaturedMakers, AdminContactInfo } from "@/pages/AdminExtras";
import { AdminReturnPolicy } from "@/pages/AdminReturnPolicy";
import { AdminHomeAd } from "@/pages/AdminHomeAd";
import { AdminGIItems } from "@/pages/AdminGIItems";

function Stat({ label, value }) {
  return (
    <div className="stat" data-testid={`admin-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AdminOverview() {
  const [data, setData] = useState({ stats: {}, fees: {}, twilio: {} });
  useEffect(() => { api.get("/admin/overview").then(r => setData(r.data)).catch(err => toast.error(formatError(err))); }, []);
  const { stats, fees, twilio } = data;
  return (
    <Page title="Admin overview" subtitle="Track the health of your GI network at a glance.">
      <section className="welcome-band">
        <div>
          <p className="eyebrow light">TRUE VISION CONTROL ROOM</p>
          <h2>Set the tone. Keep it honest.</h2>
          <p>Approve sellers, set fees, publish notifications, manage categories and OTP settings.</p>
        </div>
        <div className="welcome-symbol">✦</div>
      </section>
      <div className="stats">
        <Stat label="Sellers" value={stats.sellers || 0} />
        <Stat label="Verified" value={stats.verified || 0} />
        <Stat label="Products" value={stats.products || 0} />
        <Stat label="Orders" value={stats.orders || 0} />
      </div>
      <div className="admin-cards">
        <div className="admin-tile">
          <p className="eyebrow">CURRENT FEES</p>
          <h3>₹{fees.registration}<small> registration</small></h3>
          <p>₹{fees.subscription} monthly · {fees.marketplace_margin}% marketplace margin</p>
          <NavLink to="/admin/fees" className="arrow-link">Change fees →</NavLink>
        </div>
        <div className="admin-tile">
          <p className="eyebrow">CATEGORIES · NOTIFICATIONS</p>
          <h3>{stats.categories || 0}<small> categories · {stats.notifications || 0} active alerts</small></h3>
          <p>Curate what appears on the storefront category strip and the notifications ticker.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <NavLink to="/admin/categories" className="arrow-link">Categories →</NavLink>
            <NavLink to="/admin/notifications" className="arrow-link">Notifications →</NavLink>
          </div>
        </div>
        <div className="admin-tile">
          <p className="eyebrow">OTP / TWILIO</p>
          <h3>{twilio.enabled ? "Enabled" : (twilio.configured ? "Configured" : "Not configured")}</h3>
          <p>{twilio.sms_ready ? "Order SMS + login OTP will send when enabled." : "Fill Twilio credentials to switch on SMS + phone OTP."}</p>
          <NavLink to="/admin/otp" className="arrow-link">Manage OTP →</NavLink>
        </div>
      </div>
    </Page>
  );
}

function AdminSellers() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(r => setUsers(r.data)).catch(err => toast.error(formatError(err))); }, []);
  const toggleVerify = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}/verify`, { verified: !u.verified });
      setUsers(users.map(x => x.id === u.id ? { ...x, verified: !u.verified } : x));
      toast.success(!u.verified ? "Seller approved" : "Approval revoked");
    } catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete seller "${u.name}" (${u.email})? This will also delete their profile, products, submissions and orders. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(x => x.filter(y => y.id !== u.id));
      toast.success("Seller deleted");
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Page title="Sellers" subtitle="Approve authorised users and manage their access.">
      <div className="admin-table" data-testid="admin-sellers-table">
        <div className="admin-row admin-head"><span>Name</span><span>Email</span><span>Phone</span><span>Status</span><span></span></div>
        {users.map(u => (
          <div className="admin-row" key={u.id} data-testid={`admin-seller-${u.id}`}>
            <span>{u.name}</span>
            <span>{u.email}</span>
            <span>{u.phone || "—"}</span>
            <span className={u.verified ? "pill green" : "pill amber"}>{u.verified ? "Verified" : "Pending"}</span>
            <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className={u.verified ? "secondary-btn" : "primary-btn"} onClick={() => toggleVerify(u)} data-testid={`admin-verify-toggle-${u.id}`}>
                {u.verified ? (<><XCircle size={14} /> Revoke</>) : (<><CheckCircle2 size={14} /> Approve</>)}
              </button>
              <button className="danger-btn" onClick={() => remove(u)} data-testid={`admin-delete-seller-${u.id}`}>
                <Trash2 size={13} /> Delete
              </button>
            </span>
          </div>
        ))}
        {!users.length && <div className="admin-row"><span>No sellers yet.</span></div>}
      </div>
    </Page>
  );
}

function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [exporting, setExporting] = useState(false);
  useEffect(() => { api.get("/admin/submissions").then(r => setItems(r.data)).catch(err => toast.error(formatError(err))); }, []);
  const toggle = (id) => setExpanded(x => ({ ...x, [id]: !x[id] }));
  const downloadPdf = async (sub) => {
    try { await downloadBlob(`/submissions/${sub.id}/pdf`, `${sub.form_type}-${sub.id.slice(0, 8)}.pdf`); toast.success("PDF downloaded"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (sub) => {
    if (!window.confirm(`Delete this ${sub.form_type} submission for ${sub.user_name}? This cannot be undone.`)) return;
    try { await api.delete(`/submissions/${sub.id}`); setItems(x => x.filter(y => y.id !== sub.id)); toast.success("Submission deleted"); }
    catch (err) { toast.error(formatError(err)); }
  };
  const exportExcel = async () => {
    setExporting(true);
    try { await downloadBlob(`/admin/submissions/export.xlsx`, `truevision-submissions.xlsx`); toast.success("Excel report downloaded"); }
    catch (err) { toast.error(formatError(err)); }
    finally { setExporting(false); }
  };
  return (
    <Page title="Submissions" subtitle="Every filled application, ready for follow-up. Download signed-ready PDFs or export the full report.">
      <div className="admin-toolbar">
        <span className="muted" data-testid="submissions-count">{items.length} submission{items.length === 1 ? "" : "s"}</span>
        <button className="primary-btn" onClick={exportExcel} disabled={exporting || !items.length} data-testid="admin-export-excel">
          {exporting ? "Preparing…" : <>Download Excel report <Download size={13} /></>}
        </button>
      </div>
      <div className="admin-table" data-testid="admin-submissions-table">
        <div className="admin-row admin-head"><span>Form</span><span>Applicant</span><span>Email</span><span>Status</span><span>Date</span></div>
        {items.map(s => {
          const docs = s.documents || {};
          const docCount = Object.values(docs).filter(d => d && d.path).length;
          return (
            <div key={s.id}>
              <div className="admin-row" onClick={() => toggle(s.id)} style={{ cursor: "pointer" }} data-testid={`admin-submission-${s.id}`}>
                <span>{s.form_type}</span><span>{s.user_name}</span><span>{s.user_email}</span>
                <span className="pill amber">{s.status}</span>
                <span>{new Date(s.created_at).toLocaleDateString()} {docCount > 0 && <b style={{ color: "#0B6B3F", marginLeft: 4 }}>· {docCount} docs</b>}</span>
              </div>
              {expanded[s.id] && (
                <div className="admin-submission-detail" data-testid={`submission-detail-${s.id}`}>
                  <div className="admin-submission-grid">
                    <div>
                      <b>Form fields</b>
                      <dl>
                        {Object.entries(s.form_data || {}).map(([k, v]) => (
                          <div key={k}><dt>{k.replace(/_/g, " ")}</dt><dd>{String(v || "—")}</dd></div>
                        ))}
                      </dl>
                      <button className="primary-btn" style={{ marginTop: 12 }} onClick={() => downloadPdf(s)} data-testid={`admin-download-pdf-${s.id}`}>
                        <Download size={13} /> Download {s.form_type} PDF
                      </button>
                      <button className="danger-btn" style={{ marginTop: 12, marginLeft: 8 }} onClick={() => remove(s)} data-testid={`admin-delete-sub-${s.id}`}>
                        <Trash2 size={13} /> Delete submission
                      </button>
                    </div>
                    <div>
                      <b>Documents ({docCount})</b>
                      {docCount === 0 && <p className="muted">No documents attached.</p>}
                      <ul className="admin-doc-list">
                        {Object.entries(docs).map(([k, d]) => {
                          if (!d || !d.path) return null;
                          return (
                            <li key={k} data-testid={`admin-doc-${s.id}-${k}`}>
                              <span className="admin-doc-name">{k.replace(/_/g, " ")}</span>
                              <a href={fileUrl(d.path)} target="_blank" rel="noopener noreferrer" download={d.name || k} className="secondary-btn">
                                <Download size={12} /> Download
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!items.length && <div className="admin-row"><span>No submissions yet.</span></div>}
      </div>
    </Page>
  );
}

function AdminOrders() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/orders").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);
  const setStatus = async (o, status) => {
    try { await api.put(`/orders/${o.id}/status`, { status }); toast.success(`Order marked ${status}`); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete order for ${o.buyer_name} (${o.product_name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      setItems(x => x.filter(y => y.id !== o.id));
      toast.success("Order deleted");
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Page title="Orders" subtitle="COD orders placed by customers on the storefront.">
      <div className="admin-table" data-testid="admin-orders-table">
        <div className="admin-row admin-head"><span>Product</span><span>Buyer</span><span>Phone</span><span>Total</span><span>Status</span><span></span></div>
        {items.map(o => (
          <div className="admin-row" key={o.id} data-testid={`admin-order-${o.id}`}>
            <span>{o.product_name} × {o.quantity}</span>
            <span>{o.buyer_name}<br /><small className="muted">{o.buyer_email}</small></span>
            <span>{o.buyer_phone}</span>
            <span>₹{Number(o.total).toLocaleString("en-IN")}</span>
            <span>
              <select value={o.status} onChange={e => setStatus(o, e.target.value)} data-testid={`order-status-${o.id}`}>
                {["Placed", "Confirmed", "Shipped", "Delivered", "Cancelled"].map(s => <option key={s}>{s}</option>)}
              </select>
            </span>
            <span>
              <button className="danger-btn" onClick={() => remove(o)} data-testid={`admin-delete-order-${o.id}`}>
                <Trash2 size={13} /> Delete
              </button>
            </span>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No orders yet.</span></div>}
      </div>
    </Page>
  );
}

function AdminFees() {
  const [f, setF] = useState({ registration: 0, subscription: 0, marketplace_margin: 0 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/fees").then(r => setF({ registration: r.data.registration, subscription: r.data.subscription, marketplace_margin: r.data.marketplace_margin })).catch(err => toast.error(formatError(err))); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/admin/fees", { registration: Number(f.registration), subscription: Number(f.subscription), marketplace_margin: Number(f.marketplace_margin) });
      toast.success("Fees updated");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Page title="Fees" subtitle="Set what sellers pay to join and remain listed. Changes apply immediately.">
      <form className="form-section" onSubmit={save}>
        <div className="field-grid three">
          <label>Registration fee (₹)<input type="number" min="0" step="1" data-testid="admin-fee-registration" value={f.registration} onChange={e => setF({ ...f, registration: e.target.value })} required /></label>
          <label>Monthly subscription (₹)<input type="number" min="0" step="1" data-testid="admin-fee-subscription" value={f.subscription} onChange={e => setF({ ...f, subscription: e.target.value })} required /></label>
          <label>Marketplace margin (%)<input type="number" min="0" max="100" step="0.1" data-testid="admin-fee-margin" value={f.marketplace_margin} onChange={e => setF({ ...f, marketplace_margin: e.target.value })} required /></label>
        </div>
        <div className="form-actions">
          <span className="muted">Public pricing page reflects these values.</span>
          <button className="primary-btn" data-testid="admin-fees-save" disabled={busy}>{busy ? "Saving…" : <>Save fees <Save size={15} /></>}</button>
        </div>
      </form>
    </Page>
  );
}

function AdminTwilio() {
  const [f, setF] = useState({ account_sid: "", auth_token: "", verify_service_sid: "", from_number: "", enabled: false, auth_token_set: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/twilio").then(r => setF(prev => ({ ...prev, ...r.data, auth_token: "" }))).catch(err => toast.error(formatError(err))); }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/admin/twilio", { account_sid: f.account_sid, auth_token: f.auth_token, verify_service_sid: f.verify_service_sid, from_number: f.from_number, enabled: f.enabled });
      toast.success("Twilio settings saved");
      setF(prev => ({ ...prev, auth_token: "", auth_token_set: prev.auth_token_set || !!prev.auth_token }));
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <Page title="OTP / Twilio" subtitle="Enable phone OTP sign-in AND order SMS notifications by connecting Twilio.">
      <div className="notice">
        <ShieldCheck size={20} />
        <div>
          <b>Twilio credentials</b>
          <p>Create a Verify service (for OTP) and get a Messaging phone number (for order SMS). Paste values below and toggle Enable.</p>
        </div>
      </div>
      <form className="form-section" onSubmit={save}>
        <div className="field-grid">
          <label>Twilio Account SID<input data-testid="admin-twilio-sid" value={f.account_sid} onChange={e => setF({ ...f, account_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxxxxxxx" /></label>
          <label>Twilio Verify Service SID<input data-testid="admin-twilio-service" value={f.verify_service_sid} onChange={e => setF({ ...f, verify_service_sid: e.target.value })} placeholder="VAxxxxxxxxxxxxxxxxxxxxx" /></label>
          <label>Auth Token {f.auth_token_set && <small className="muted"> · stored (leave blank to keep)</small>}
            <input data-testid="admin-twilio-token" type="password" value={f.auth_token} onChange={e => setF({ ...f, auth_token: e.target.value })} placeholder={f.auth_token_set ? "•••••••• (stored)" : "Auth token"} />
          </label>
          <label>Messaging From-number<input data-testid="admin-twilio-from" value={f.from_number} onChange={e => setF({ ...f, from_number: e.target.value })} placeholder="+1415…" /></label>
        </div>
        <label className="check-line">
          <input type="checkbox" data-testid="admin-twilio-enabled" checked={f.enabled} onChange={e => setF({ ...f, enabled: e.target.checked })} />
          <span>Enable phone OTP + order SMS</span>
        </label>
        <div className="form-actions">
          <span className="muted">Auth token is stored server-side and never sent back to the browser.</span>
          <button className="primary-btn" data-testid="admin-twilio-save" disabled={busy}>{busy ? "Saving…" : <>Save Twilio settings <Send size={15} /></>}</button>
        </div>
      </form>
    </Page>
  );
}

function AdminCategories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/categories").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, { name: name.trim(), slug: "", icon: "" });
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", { name: name.trim(), slug: "", icon: "" });
        toast.success("Category added");
      }
      setName(""); setEditing(null); load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <Page title="Categories" subtitle="Curate the master list of product categories. Sellers pick from this list.">
      <form className="inline-form" onSubmit={submit}>
        <input placeholder={editing ? "Edit category name" : "New category name"} value={name} onChange={e => setName(e.target.value)} data-testid="category-name-input" />
        <button className="primary-btn" disabled={busy} data-testid="category-save-button">{editing ? "Update" : "Add"}</button>
        {editing && <button type="button" className="text-btn" onClick={() => { setEditing(null); setName(""); }} data-testid="category-cancel-edit">Cancel</button>}
      </form>
      <div className="admin-table" data-testid="admin-categories-table">
        <div className="admin-row admin-head"><span>Name</span><span>Slug</span><span></span></div>
        {items.map(c => (
          <div className="admin-row" key={c.id} data-testid={`category-${c.id}`}>
            <span>{c.name}</span><span className="muted">{c.slug}</span>
            <span style={{ display: "flex", gap: 8 }}>
              <button className="secondary-btn" onClick={() => { setEditing(c); setName(c.name); }} data-testid={`category-edit-${c.id}`}><Pencil size={12} /> Edit</button>
              <button className="danger-btn" onClick={() => remove(c)} data-testid={`category-delete-${c.id}`}><Trash2 size={12} /> Delete</button>
            </span>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No categories yet.</span></div>}
      </div>
    </Page>
  );
}

function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", link_url: "", priority: "info", is_active: true });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/notifications").then(r => setItems(r.data)).catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/admin/notifications/${editing.id}`, form);
        toast.success("Notification updated");
      } else {
        await api.post("/admin/notifications", form);
        toast.success("Notification published");
      }
      setForm({ title: "", message: "", link_url: "", priority: "info", is_active: true });
      setEditing(null);
      load();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const remove = async (n) => {
    if (!window.confirm(`Delete notification "${n.title}"?`)) return;
    try { await api.delete(`/admin/notifications/${n.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  const edit = (n) => { setEditing(n); setForm({ title: n.title, message: n.message, link_url: n.link_url || "", priority: n.priority || "info", is_active: n.is_active }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Page title="Notifications" subtitle="Manage the banner ticker that plays on the storefront home page.">
      <form className="form-section" onSubmit={submit} style={{ marginBottom: 20 }}>
        <div className="field-grid">
          <label>Title<input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="notif-title" placeholder="e.g., NEW SCHEME" /></label>
          <label>Priority
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="notif-priority">
              <option value="info">Info</option><option value="warn">Warning</option><option value="success">Success</option>
            </select>
          </label>
        </div>
        <label>Message<textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="notif-message" placeholder="Short message to scroll on the home page" /></label>
        <label>Link (optional)<input value={form.link_url} onChange={e => setForm({ ...form, link_url: e.target.value })} data-testid="notif-link" placeholder="https://…" /></label>
        <label className="check-line">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} data-testid="notif-active" />
          <span>Show on home page</span>
        </label>
        <div className="form-actions">
          <span className="muted">Ticker updates immediately.</span>
          <button className="primary-btn" disabled={busy} data-testid="notif-save-button">{editing ? "Update notification" : <><Plus size={13} /> Publish notification</>}</button>
        </div>
        {editing && <button type="button" className="text-btn" onClick={() => { setEditing(null); setForm({ title: "", message: "", link_url: "", priority: "info", is_active: true }); }}>Cancel edit</button>}
      </form>

      <div className="admin-table" data-testid="admin-notifications-table">
        <div className="admin-row admin-head"><span>Title</span><span>Message</span><span>Status</span><span></span><span></span></div>
        {items.map(n => (
          <div className="admin-row" key={n.id} data-testid={`notif-${n.id}`}>
            <span>{n.title}</span>
            <span className="muted">{n.message}</span>
            <span className={n.is_active ? "pill green" : "pill red"}>{n.is_active ? "Active" : "Hidden"}</span>
            <button className="secondary-btn" onClick={() => edit(n)} data-testid={`notif-edit-${n.id}`}><Pencil size={12} /> Edit</button>
            <button className="danger-btn" onClick={() => remove(n)} data-testid={`notif-delete-${n.id}`}><Trash2 size={12} /> Delete</button>
          </div>
        ))}
        {!items.length && <div className="admin-row"><span>No notifications yet.</span></div>}
      </div>
    </Page>
  );
}

export default function AdminRouter() {
  return (
    <Routes>
      <Route path="/" element={<AdminOverview />} />
      <Route path="/sellers" element={<AdminSellers />} />
      <Route path="/submissions" element={<AdminSubmissions />} />
      <Route path="/orders" element={<AdminOrders />} />
      <Route path="/fees" element={<AdminFees />} />
      <Route path="/categories" element={<AdminCategories />} />
      <Route path="/notifications" element={<AdminNotifications />} />
      <Route path="/banners" element={<AdminBanners />} />
      <Route path="/gallery" element={<AdminGallery />} />
      <Route path="/counters" element={<AdminCounters />} />
      <Route path="/about" element={<AdminAbout />} />
      <Route path="/contact" element={<AdminContact />} />
      <Route path="/contact-info" element={<AdminContactInfo />} />
      <Route path="/featured-makers" element={<AdminFeaturedMakers />} />
      <Route path="/return-policy" element={<AdminReturnPolicy />} />
      <Route path="/ads" element={<AdminHomeAd />} />
      <Route path="/gi-items" element={<AdminGIItems />} />
      <Route path="/authorised-users" element={<AdminAuthorisedUsers />} />
      <Route path="/otp" element={<AdminTwilio />} />
    </Routes>
  );
}
